import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface AlarmPopup {
  id: number;
  level: "danger" | "warning" | "info";
  title: string;
  location: string;
  time: string;
  detail: string;
  visible: boolean;
}

interface AIPredictionPopup {
  visible: boolean;
  type: string;
  confidence: number;
  description: string;
  suggestion: string;
  location: string;
}

// Real-time flow data generator
function generateFlowPoint(base: number, noise: number) {
  return base + (Math.random() - 0.5) * noise;
}

const revenueData = [
  { month: "1月", value: 725, pred: false }, { month: "2月", value: 685, pred: false },
  { month: "3月", value: 715, pred: false }, { month: "4月", value: 745, pred: false },
  { month: "5月", value: 785, pred: false }, { month: "6月", value: 805, pred: false },
  { month: "7月", value: 775, pred: false }, { month: "8月", value: 815, pred: false },
  { month: "9月", value: 835, pred: false }, { month: "10月", value: 845, pred: false },
  { month: "11月", value: 825, pred: false }, { month: "12月", value: 856, pred: false },
  { month: "1月(预)", value: 880, pred: true }, { month: "2月(预)", value: 895, pred: true },
  { month: "3月(预)", value: 910, pred: true },
];

const alarmData = [
  { type: "泄漏报警", count: 5, color: "#ef4444" },
  { type: "欠费预警", count: 12, color: "#f59e0b" },
  { type: "设备异常", count: 6, color: "#f97316" },
];

const regionData = [
  { name: "朝阳区", users: 32456, revenue: 2856320, rate: 98.2 },
  { name: "海淀区", users: 28123, revenue: 2456210, rate: 97.8 },
  { name: "西城区", users: 18956, revenue: 1856320, rate: 99.1 },
  { name: "东城区", users: 15234, revenue: 1456210, rate: 98.5 },
  { name: "丰台区", users: 22456, revenue: 1956320, rate: 97.3 },
  { name: "石景山区", users: 11231, revenue: 980760, rate: 96.8 },
];

const liveAlarms = [
  { time: "09:23", msg: "朝阳区望京 - 燃气泄漏报警", level: "danger" },
  { time: "08:45", msg: "西城区西单 - 压力异常超限", level: "danger" },
  { time: "08:30", msg: "海淀区中关村 - 余额不足预警", level: "warning" },
  { time: "07:15", msg: "丰台区方庄 - 设备离线超24h", level: "warning" },
  { time: "06:50", msg: "朝阳区亮马桥 - 用气量异常", level: "info" },
  { time: "06:20", msg: "东城区东直门 - 阀门状态异常", level: "warning" },
];

const pressureData = [
  { name: "朝阳A", value: 0.35, max: 0.4, status: "正常" },
  { name: "海淀B", value: 0.28, max: 0.4, status: "正常" },
  { name: "东城C", value: 0.41, max: 0.4, status: "超压" },
  { name: "丰台D", value: 0.22, max: 0.4, status: "正常" },
];

const alarmPopupPool: Omit<AlarmPopup, "id" | "visible">[] = [
  { level: "danger", title: "燃气泄漏报警", location: "朝阳区望京街道1号楼", time: "", detail: "传感器检测到甲烷浓度超标，当前浓度：2.8% LEL，已超过报警阈值（1% LEL）", suggestion: "立即关闭阀门，疏散人员，拨打应急热线" },
  { level: "danger", title: "管网压力超限", location: "东城区东直门调压站C", time: "", detail: "出口压力 0.41 MPa，超过额定压力 0.40 MPa，超限 2.5%", suggestion: "远程调节调压阀，派员现场检查" },
  { level: "warning", title: "设备离线告警", location: "丰台区方庄小区燃气表", time: "", detail: "设备编号 IC-2024-008821 已离线超过 24 小时，最后在线时间：昨日 08:32", suggestion: "安排维修人员上门检查设备" },
  { level: "warning", title: "用气量异常波动", location: "海淀区中关村商业区", time: "", detail: "过去1小时用气量较历史同期高出 38%，疑似管道泄漏或非法用气", suggestion: "AI已启动异常溯源分析，预计5分钟内出结果" },
  { level: "info", title: "余额不足预警", location: "西城区西单北大街56号", time: "", detail: "用户张建国（R001）账户余额 ¥12.50，预计 3 天后用尽", suggestion: "系统已自动发送短信提醒，可手动催缴" },
];

const aiPredictionPool: Omit<AIPredictionPopup, "visible">[] = [
  { type: "管道腐蚀风险", confidence: 87, location: "朝阳区望京管网 A-12 段", description: "AI模型基于近6个月压力波动数据、管龄（18年）及土壤腐蚀指数，预测该管段在未来30天内发生腐蚀泄漏的概率为 87%。", suggestion: "建议本周内安排管道内检测，优先更换高风险管段" },
  { type: "设备故障预测", confidence: 73, location: "海淀区中关村调压站 B", description: "调压阀振动频率异常，AI检测到近7天振动幅度增加 42%，与历史故障前特征高度吻合。", suggestion: "建议48小时内进行预防性维护，更换密封件" },
  { type: "欠费风险预测", confidence: 91, location: "丰台区方庄小区（28户）", description: "AI综合用气量、历史缴费记录、季节因素，预测本月末将有28户出现欠费，预计欠费金额 ¥15,680。", suggestion: "建议提前3天发送催缴短信，对高风险用户开启自动代扣" },
  { type: "用气高峰预警", confidence: 82, location: "全区域", description: "根据天气预报（明日最低气温 -3°C）及历史数据，AI预测明日早高峰（07:00-09:00）用气量将达 3,850 m³/h，超过管网设计容量 92%。", suggestion: "建议提前调整调压站出口压力，启动备用供气通道" },
];

export default function ScreenPage() {
  const navigate = useNavigate();
  const [time, setTime] = useState(new Date());
  const [liveRevenue, setLiveRevenue] = useState(856234.50);
  const [liveUsers, setLiveUsers] = useState(128456);
  const [liveAlarmIdx, setLiveAlarmIdx] = useState(0);
  const [liveGasFlow, setLiveGasFlow] = useState(2856.4);
  const [alarmPopups, setAlarmPopups] = useState<AlarmPopup[]>([]);
  const [aiPrediction, setAIPrediction] = useState<AIPredictionPopup | null>(null);
  const [alarmCounter, setAlarmCounter] = useState(0);
  const [showAlarmDetail, setShowAlarmDetail] = useState<AlarmPopup | null>(null);
  const [gisLayer, setGisLayer] = useState<"pipe" | "heatmap" | "pressure">("pipe");
  const [liveRegionData, setLiveRegionData] = useState([
    { name: "朝阳区", usage: 856.3, rank: 1, trend: "up", delta: 12.4 },
    { name: "海淀区", usage: 742.1, rank: 2, trend: "up", delta: 8.2 },
    { name: "丰台区", usage: 589.6, rank: 3, trend: "down", delta: -3.1 },
    { name: "西城区", usage: 512.8, rank: 4, trend: "up", delta: 5.7 },
    { name: "东城区", usage: 423.5, rank: 5, trend: "down", delta: -1.8 },
    { name: "石景山区", usage: 298.2, rank: 6, trend: "up", delta: 2.3 },
  ]);
  const [aiConfidenceHistory, setAiConfidenceHistory] = useState<number[]>([72, 75, 78, 74, 80, 83, 79, 85, 87, 82, 88, 91]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const revenueCanvasRef = useRef<HTMLCanvasElement>(null);
  const pressureCanvasRef = useRef<HTMLCanvasElement>(null);
  const flowCanvasRef = useRef<HTMLCanvasElement>(null);
  const gasCanvasRef = useRef<HTMLCanvasElement>(null);
  const confidenceCanvasRef = useRef<HTMLCanvasElement>(null);
  const pressureAnimRef = useRef<number>(0);
  const flowAnimRef = useRef<number>(0);
  const pressurePhaseRef = useRef(0);
  const flowPhaseRef = useRef(0);
  // Real-time flow history (last 60 points)
  const flowHistoryRef = useRef<number[]>(Array.from({ length: 60 }, (_, i) => generateFlowPoint(2856 + Math.sin(i * 0.2) * 80, 40)));
  const gasHistoryRef = useRef<number[]>(Array.from({ length: 60 }, (_, i) => generateFlowPoint(285 + Math.sin(i * 0.15) * 20, 10)));

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setLiveRevenue(prev => prev + Math.random() * 800 + 200);
      setLiveUsers(prev => prev + (Math.random() > 0.7 ? 1 : 0));
      setLiveGasFlow(prev => prev + (Math.random() - 0.5) * 10);
      // Real-time region ranking update
      setLiveRegionData(prev => prev.map(r => {
        const delta = (Math.random() - 0.45) * 3;
        const newUsage = Math.max(100, r.usage + delta);
        return { ...r, usage: parseFloat(newUsage.toFixed(1)), trend: delta >= 0 ? "up" : "down", delta: parseFloat(Math.abs(delta).toFixed(1)) };
      }).sort((a, b) => b.usage - a.usage).map((r, i) => ({ ...r, rank: i + 1 })));
      // AI confidence trend update
      setAiConfidenceHistory(prev => {
        const last = prev[prev.length - 1];
        const next = Math.min(99, Math.max(60, last + (Math.random() - 0.4) * 4));
        return [...prev.slice(-19), parseFloat(next.toFixed(1))];
      });
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setLiveAlarmIdx(prev => (prev + 1) % liveAlarms.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  // Real-time alarm popup - triggers every 18s
  useEffect(() => {
    const timer = setInterval(() => {
      setAlarmCounter(prev => {
        const idx = prev % alarmPopupPool.length;
        const pool = alarmPopupPool[idx];
        const now = new Date();
        const timeStr = now.toLocaleTimeString("zh-CN", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
        const newAlarm: AlarmPopup = { ...pool, id: Date.now(), time: timeStr, visible: true };
        setAlarmPopups(current => [...current.slice(-2), newAlarm]);
        // Auto dismiss after 8s
        setTimeout(() => {
          setAlarmPopups(current => current.map(a => a.id === newAlarm.id ? { ...a, visible: false } : a));
        }, 8000);
        return prev + 1;
      });
    }, 18000);
    // Trigger first alarm after 3s
    const initTimer = setTimeout(() => {
      const pool = alarmPopupPool[0];
      const now = new Date();
      const timeStr = now.toLocaleTimeString("zh-CN", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
      const newAlarm: AlarmPopup = { ...pool, id: Date.now(), time: timeStr, visible: true };
      setAlarmPopups([newAlarm]);
      setTimeout(() => {
        setAlarmPopups(current => current.map(a => a.id === newAlarm.id ? { ...a, visible: false } : a));
      }, 8000);
    }, 3000);
    return () => { clearInterval(timer); clearTimeout(initTimer); };
  }, []);

  // AI prediction popup - triggers every 30s
  useEffect(() => {
    const timer = setInterval(() => {
      setAlarmCounter(prev => {
        const idx = prev % aiPredictionPool.length;
        setAIPrediction({ ...aiPredictionPool[idx], visible: true });
        return prev;
      });
    }, 30000);
    // Trigger first AI prediction after 10s
    const initTimer = setTimeout(() => {
      setAIPrediction({ ...aiPredictionPool[1], visible: true });
    }, 10000);
    return () => { clearInterval(timer); clearTimeout(initTimer); };
  }, []);

  // GIS Map
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;

    ctx.fillStyle = "#0a1628";
    ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = "rgba(255,255,255,0.03)";
    ctx.lineWidth = 1;
    for (let x = 0; x < w; x += 35) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
    for (let y = 0; y < h; y += 35) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }

    const pipes = [
      [20, 70, 180, 70], [180, 70, 320, 55], [180, 70, 200, 150], [200, 150, 360, 140],
      [360, 140, 430, 90], [60, 70, 70, 160], [70, 160, 200, 150], [70, 160, 90, 230],
      [90, 230, 280, 220], [280, 220, 360, 140], [280, 220, 290, 280], [290, 280, 430, 270],
      [430, 90, 480, 120], [430, 270, 480, 250],
    ];
    pipes.forEach(([x1, y1, x2, y2]) => {
      const g = ctx.createLinearGradient(x1, y1, x2, y2);
      g.addColorStop(0, "rgba(232,83,26,0.9)");
      g.addColorStop(0.5, "rgba(232,83,26,0.6)");
      g.addColorStop(1, "rgba(232,83,26,0.3)");
      ctx.strokeStyle = g;
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    });

    const users = [
      [50, 68], [110, 65], [155, 72], [230, 145], [275, 138], [340, 135], [400, 88],
      [80, 158], [140, 162], [100, 228], [190, 222], [260, 218], [300, 278], [390, 265],
      [460, 115], [465, 258],
    ];
    users.forEach(([x, y], i) => {
      const isAlarm = i === 2 || i === 6 || i === 11;
      ctx.beginPath();
      ctx.arc(x, y, isAlarm ? 5 : 3, 0, Math.PI * 2);
      ctx.fillStyle = isAlarm ? "#ef4444" : "#22c55e";
      ctx.fill();
      if (isAlarm) {
        ctx.beginPath();
        ctx.arc(x, y, 9, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(239,68,68,0.4)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    });

    [[180, 70], [200, 150], [360, 140]].forEach(([x, y]) => {
      ctx.beginPath();
      ctx.arc(x, y, 7, 0, Math.PI * 2);
      ctx.fillStyle = "#E8531A";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, y, 13, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(232,83,26,0.35)";
      ctx.lineWidth = 2;
      ctx.stroke();
    });

    [[70, 160], [280, 220], [430, 90]].forEach(([x, y]) => {
      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fillStyle = "#06b6d4";
      ctx.fill();
      ctx.fillStyle = "white";
      ctx.font = "bold 7px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("P", x, y + 2.5);
    });

    [[90, 115], [250, 185], [380, 200]].forEach(([x, y]) => {
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fillStyle = "#22c55e";
      ctx.fill();
      ctx.fillStyle = "white";
      ctx.font = "bold 6px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("V", x, y + 2);
    });
  }, []);

  // Revenue trend with AI prediction
  useEffect(() => {
    const canvas = revenueCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const maxV = Math.max(...revenueData.map(d => d.value)) + 50;
    const barW = (w - 20) / revenueData.length;

    revenueData.forEach((d, i) => {
      const barH = ((d.value / maxV) * (h - 30));
      const x = 10 + i * barW + barW * 0.1;
      const bw = barW * 0.8;
      const y = h - 20 - barH;

      if (d.pred) {
        ctx.setLineDash([3, 3]);
        ctx.strokeStyle = "rgba(232,83,26,0.6)";
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, bw, barH);
        ctx.setLineDash([]);
        ctx.fillStyle = "rgba(232,83,26,0.15)";
        ctx.fillRect(x, y, bw, barH);
      } else {
        const grad = ctx.createLinearGradient(x, y, x, h - 20);
        grad.addColorStop(0, i === 11 ? "rgba(232,83,26,1)" : "rgba(232,83,26,0.7)");
        grad.addColorStop(1, "rgba(232,83,26,0.1)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.roundRect(x, y, bw, barH, 2);
        ctx.fill();
      }

      ctx.fillStyle = d.pred ? "rgba(232,83,26,0.5)" : "rgba(255,255,255,0.3)";
      ctx.font = "7px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(d.month.replace("月", ""), x + bw / 2, h - 6);
    });

    ctx.fillStyle = "rgba(232,83,26,0.7)";
    ctx.font = "9px sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("实际营收", 10, 12);
    ctx.setLineDash([3, 3]);
    ctx.strokeStyle = "rgba(232,83,26,0.6)";
    ctx.strokeRect(75, 4, 10, 8);
    ctx.setLineDash([]);
    ctx.fillText("AI预测", 90, 12);
  }, []);

  // Animated pressure monitoring
  const drawPressure = useCallback(() => {
    const canvas = pressureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    pressurePhaseRef.current += 0.03;
    const phase = pressurePhaseRef.current;

    pressureData.forEach((p, i) => {
      const x = 20 + i * (w / pressureData.length);
      const cx = x + (w / pressureData.length) / 2 - 10;
      const cy = h / 2;
      const r = 28;
      // Animate pressure value slightly
      const animValue = p.value + Math.sin(phase + i * 1.2) * 0.01;
      const pct = animValue / p.max;
      const isAlert = p.status !== "正常";

      // Outer glow ring for alert
      if (isAlert) {
        const glowAlpha = 0.3 + 0.2 * Math.sin(phase * 2);
        ctx.beginPath();
        ctx.arc(cx, cy, r + 6, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(239,68,68,${glowAlpha})`;
        ctx.lineWidth = 3;
        ctx.stroke();
      }

      // Background arc
      ctx.beginPath();
      ctx.arc(cx, cy, r, Math.PI * 0.75, Math.PI * 2.25);
      ctx.strokeStyle = "rgba(255,255,255,0.08)";
      ctx.lineWidth = 6;
      ctx.stroke();

      // Value arc with gradient
      const arcGrad = ctx.createLinearGradient(cx - r, cy, cx + r, cy);
      if (isAlert) {
        arcGrad.addColorStop(0, "#f97316");
        arcGrad.addColorStop(1, "#ef4444");
      } else {
        arcGrad.addColorStop(0, "#06b6d4");
        arcGrad.addColorStop(1, "#22c55e");
      }
      ctx.beginPath();
      ctx.arc(cx, cy, r, Math.PI * 0.75, Math.PI * 0.75 + (Math.PI * 1.5 * Math.min(pct, 1)));
      ctx.strokeStyle = arcGrad;
      ctx.lineWidth = 6;
      ctx.lineCap = "round";
      ctx.stroke();

      // Tick marks
      for (let t = 0; t <= 10; t++) {
        const tickAngle = Math.PI * 0.75 + (Math.PI * 1.5 * t / 10);
        const innerR = r - 8;
        const outerR = r - 4;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(tickAngle) * innerR, cy + Math.sin(tickAngle) * innerR);
        ctx.lineTo(cx + Math.cos(tickAngle) * outerR, cy + Math.sin(tickAngle) * outerR);
        ctx.strokeStyle = "rgba(255,255,255,0.2)";
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Center value
      ctx.fillStyle = isAlert ? "#ef4444" : "#06b6d4";
      ctx.font = "bold 11px monospace";
      ctx.textAlign = "center";
      ctx.fillText(animValue.toFixed(2), cx, cy + 3);
      ctx.fillStyle = "rgba(255,255,255,0.4)";
      ctx.font = "7px sans-serif";
      ctx.fillText("MPa", cx, cy + 13);

      // Name
      ctx.fillStyle = isAlert ? "#fca5a5" : "rgba(255,255,255,0.7)";
      ctx.font = "bold 9px sans-serif";
      ctx.fillText(p.name, cx, cy + r + 14);

      // Status dot
      ctx.fillStyle = isAlert ? "#ef4444" : "#22c55e";
      ctx.beginPath();
      ctx.arc(cx, cy + r + 22, 3, 0, Math.PI * 2);
      ctx.fill();
    });

    pressureAnimRef.current = requestAnimationFrame(drawPressure);
  }, []);

  useEffect(() => {
    drawPressure();
    return () => cancelAnimationFrame(pressureAnimRef.current);
  }, [drawPressure]);

  // Real-time flow line chart
  const drawFlowChart = useCallback(() => {
    const canvas = flowCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    flowPhaseRef.current += 0.05;
    // Add new point
    const newVal = generateFlowPoint(2856 + Math.sin(flowPhaseRef.current * 0.3) * 120, 50);
    flowHistoryRef.current.push(newVal);
    if (flowHistoryRef.current.length > 60) flowHistoryRef.current.shift();

    const data = flowHistoryRef.current;
    const minV = Math.min(...data) - 50;
    const maxV = Math.max(...data) + 50;
    const range = maxV - minV;
    const padL = 8, padR = 8, padT = 12, padB = 16;
    const chartW = w - padL - padR;
    const chartH = h - padT - padB;

    // Grid lines
    for (let i = 0; i <= 4; i++) {
      const y = padT + (chartH / 4) * i;
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(w - padR, y);
      ctx.strokeStyle = "rgba(255,255,255,0.06)";
      ctx.lineWidth = 1;
      ctx.stroke();
      const val = maxV - (range / 4) * i;
      ctx.fillStyle = "rgba(255,255,255,0.3)";
      ctx.font = "8px monospace";
      ctx.textAlign = "left";
      ctx.fillText(val.toFixed(0), padL, y - 2);
    }

    // Gradient fill
    const grad = ctx.createLinearGradient(0, padT, 0, h - padB);
    grad.addColorStop(0, "rgba(6,182,212,0.3)");
    grad.addColorStop(1, "rgba(6,182,212,0)");
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = padL + (i / (data.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.lineTo(padL + chartW, h - padB);
    ctx.lineTo(padL, h - padB);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    // Line
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = padL + (i / (data.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "#06b6d4";
    ctx.lineWidth = 1.5;
    ctx.lineJoin = "round";
    ctx.stroke();

    // Current value dot
    const lastV = data[data.length - 1];
    const lastX = padL + chartW;
    const lastY = padT + chartH - ((lastV - minV) / range) * chartH;
    ctx.beginPath();
    ctx.arc(lastX, lastY, 3, 0, Math.PI * 2);
    ctx.fillStyle = "#06b6d4";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(lastX, lastY, 6, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(6,182,212,0.4)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Label
    ctx.fillStyle = "#06b6d4";
    ctx.font = "bold 9px monospace";
    ctx.textAlign = "right";
    ctx.fillText(`${lastV.toFixed(1)} m³/h`, w - padR, padT - 2);

    flowAnimRef.current = requestAnimationFrame(drawFlowChart);
  }, []);

  useEffect(() => {
    drawFlowChart();
    return () => cancelAnimationFrame(flowAnimRef.current);
  }, [drawFlowChart]);

  // Gas usage AI prediction comparison chart
  const drawGasChart = useCallback(() => {
    const canvas = gasCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const months = ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"];
    const actual = [312, 298, 285, 271, 245, 228, 235, 248, 268, 285, 302, 318];
    const predicted = [315, 302, 288, 275, 248, 232, 238, 252, 272, 290, 308, 325];


    const padL = 28, padR = 10, padT = 16, padB = 20;
    const chartW = w - padL - padR;
    const chartH = h - padT - padB;
    const allVals = [...actual, ...predicted, 350];
    const minV = Math.min(...allVals) - 20;
    const maxV = Math.max(...allVals) + 20;
    const range = maxV - minV;

    // Grid
    for (let i = 0; i <= 4; i++) {
      const y = padT + (chartH / 4) * i;
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(w - padR, y);
      ctx.strokeStyle = "rgba(255,255,255,0.06)";
      ctx.lineWidth = 1;
      ctx.stroke();
      const val = maxV - (range / 4) * i;
      ctx.fillStyle = "rgba(255,255,255,0.3)";
      ctx.font = "8px sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(val.toFixed(0), padL - 2, y + 3);
    }

    // Month labels
    months.forEach((m, i) => {
      const x = padL + (i / (months.length - 1)) * chartW;
      ctx.fillStyle = "rgba(255,255,255,0.3)";
      ctx.font = "7px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(m, x, h - 4);
    });

    // Actual line
    ctx.beginPath();
    actual.forEach((v, i) => {
      const x = padL + (i / (months.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "#E8531A";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.stroke();

    // Predicted line (dashed)
    ctx.beginPath();
    ctx.setLineDash([4, 3]);
    predicted.forEach((v, i) => {
      const x = padL + (i / (months.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "rgba(232,83,26,0.5)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.setLineDash([]);

    // Dots on actual
    actual.forEach((v, i) => {
      const x = padL + (i / (months.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = "#E8531A";
      ctx.fill();
    });

    // Legend
    ctx.fillStyle = "#E8531A";
    ctx.fillRect(padL, 4, 12, 3);
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "8px sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("实际用气量", padL + 15, 8);

    ctx.setLineDash([4, 3]);
    ctx.strokeStyle = "rgba(232,83,26,0.5)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(padL + 80, 5);
    ctx.lineTo(padL + 92, 5);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.fillText("AI预测值", padL + 95, 8);
  }, []);

  useEffect(() => {
    drawGasChart();
  }, [drawGasChart]);

  const maxRevenue = Math.max(...revenueData.filter(d => !d.pred).map(d => d.value));

  return (
    <div className="w-full min-h-screen bg-[#060d1f] text-white overflow-hidden relative" style={{ fontFamily: "'Orbitron', 'Noto Sans SC', sans-serif" }}>
      {/* Header */}
      <div className="relative border-b border-[#E8531A]/20 px-8 py-3 flex items-center justify-between"
        style={{ background: "linear-gradient(90deg, rgba(232,83,26,0.1) 0%, rgba(232,83,26,0.05) 50%, rgba(232,83,26,0.1) 100%)" }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#E8531A] flex items-center justify-center">
            <i className="ri-fire-line text-white text-base"></i>
          </div>
          <div>
            <div className="text-[#E8531A] text-xs tracking-widest">AI SMART GAS</div>
            <div className="text-white font-bold text-sm tracking-wider">智慧燃气营收管理系统 · 数据大屏</div>
          </div>
        </div>

        <div className="absolute left-1/2 -translate-x-1/2 text-center">
          <div className="text-2xl font-bold text-white tracking-widest">
            {time.toLocaleTimeString("zh-CN", { hour12: false })}
          </div>
          <div className="text-xs text-white/50 tracking-wider">
            {time.toLocaleDateString("zh-CN", { year: "numeric", month: "long", day: "numeric", weekday: "long" })}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
            <span className="text-green-400 text-xs">系统运行正常</span>
          </div>
          <button
            onClick={() => navigate("/")}
            className="text-white/50 hover:text-white text-xs border border-white/20 px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-logout-box-r-line mr-1"></i>退出大屏
          </button>
        </div>
      </div>

      {/* Main grid */}
      <div className="p-3 grid grid-cols-12 gap-3" style={{ height: "calc(100vh - 60px)" }}>

        {/* Left column */}
        <div className="col-span-3 flex flex-col gap-3">
          {/* Core KPIs */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "总用户数", value: liveUsers.toLocaleString(), unit: "户", icon: "ri-group-line", color: "#E8531A" },
              { label: "在线设备", value: "142,568", unit: "台", icon: "ri-cpu-line", color: "#22c55e" },
              { label: "今日营收", value: `¥${(liveRevenue / 10000).toFixed(1)}`, unit: "万", icon: "ri-money-cny-circle-line", color: "#f59e0b" },
              { label: "实时流量", value: liveGasFlow.toFixed(1), unit: "m³/h", icon: "ri-fire-line", color: "#06b6d4" },
            ].map((kpi, i) => (
              <div key={i} className="rounded-xl p-3 border border-white/10" style={{ background: "rgba(255,255,255,0.04)" }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <i className={`${kpi.icon} text-sm`} style={{ color: kpi.color }}></i>
                  <span className="text-white/50 text-[10px]">{kpi.label}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-bold" style={{ color: kpi.color }}>{kpi.value}</span>
                  <span className="text-white/40 text-xs">{kpi.unit}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Pressure Monitoring */}
          <div className="rounded-xl border border-white/10 p-3" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="text-xs text-white/60 mb-2 flex items-center gap-2">
              <i className="ri-speed-line text-cyan-400"></i>
              管网压力实时监测
              <span className="ml-auto text-[10px] text-cyan-400">MPa</span>
            </div>
            <canvas ref={pressureCanvasRef} width={280} height={90} className="w-full" />
            <div className="mt-1 flex items-center gap-3 text-[10px]">
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-cyan-400"></div><span className="text-white/50">正常</span></div>
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div><span className="text-white/50">超压告警</span></div>
            </div>
          </div>

          {/* Alarm types */}
          <div className="rounded-xl border border-white/10 p-3 flex-1" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="text-xs text-white/60 mb-2 flex items-center gap-2">
              <i className="ri-alarm-warning-line text-red-400"></i>
              告警类型分布
            </div>
            <div className="space-y-2.5">
              {alarmData.map((a, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-white/70">{a.type}</span>
                    <span className="font-bold" style={{ color: a.color }}>{a.count}</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full" style={{ width: `${(a.count / 23) * 100}%`, backgroundColor: a.color }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="text-xs text-white/60 mb-2">实时告警滚动</div>
              <div className="overflow-hidden" style={{ height: 78 }}>
                <div className="transition-transform duration-500" style={{ transform: `translateY(-${liveAlarmIdx * 26}px)` }}>
                  {[...liveAlarms, ...liveAlarms].map((a, i) => (
                    <div key={i} className="flex items-center gap-2 py-1.5" style={{ height: 26 }}>
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${a.level === "danger" ? "bg-red-500 animate-pulse" : a.level === "warning" ? "bg-amber-500" : "bg-cyan-400"}`}></div>
                      <span className="text-[10px] text-white/50">{a.time}</span>
                      <span className="text-[10px] text-white/70 truncate">{a.msg}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Device status */}
          <div className="rounded-xl border border-white/10 p-3" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="text-xs text-white/60 mb-2 flex items-center gap-2">
              <i className="ri-cpu-line text-green-400"></i>
              设备状态
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "在线", value: "138,562", color: "#22c55e", pct: 97.2 },
                { label: "离线", value: "2,856", color: "#6b7280", pct: 2.0 },
                { label: "告警", value: "23", color: "#ef4444", pct: 0.02 },
                { label: "维护", value: "1,127", color: "#f59e0b", pct: 0.79 },
              ].map((d, i) => (
                <div key={i} className="text-center">
                  <div className="text-sm font-bold" style={{ color: d.color }}>{d.value}</div>
                  <div className="text-[10px] text-white/40">{d.label} {d.pct}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Center column - GIS Map */}
        <div className="col-span-6 flex flex-col gap-3">
          {/* GIS Map */}
          <div className="flex-1 rounded-xl border border-[#E8531A]/30 overflow-hidden relative" style={{ background: "#0a1628" }}>
            <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
              <div className="bg-black/60 rounded-lg px-3 py-1.5 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                <span className="text-white text-xs">管网GIS实时监控</span>
              </div>
              <div className="flex gap-1">
                {([
                  { key: "pipe", label: "管网" },
                  { key: "heatmap", label: "热力图" },
                  { key: "pressure", label: "压力层" },
                ] as const).map(l => (
                  <button
                    key={l.key}
                    onClick={() => setGisLayer(l.key)}
                    className={`px-2 py-1 rounded text-[10px] font-medium cursor-pointer whitespace-nowrap transition-colors ${gisLayer === l.key ? "bg-[#E8531A] text-white" : "bg-black/50 text-white/60 hover:text-white"}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
              {[
                { color: "bg-[#E8531A]", label: "调压站" },
                { color: "bg-cyan-500", label: "压力监测" },
                { color: "bg-green-500", label: "阀门(开)" },
                { color: "bg-red-500", label: "告警点" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-black/50 rounded px-2 py-1">
                  <div className={`w-2 h-2 rounded-full ${item.color}`}></div>
                  <span className="text-white/70 text-[10px]">{item.label}</span>
                </div>
              ))}
            </div>
            <canvas ref={canvasRef} width={500} height={320} className="w-full h-full" style={{ objectFit: "cover", display: gisLayer === "pipe" ? "block" : "none" }} />
            {/* Heatmap overlay */}
            {gisLayer === "heatmap" && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-full h-full">
                  {/* Heatmap gradient blobs */}
                  {[
                    { x: "18%", y: "22%", size: 80, color: "rgba(239,68,68,0.55)", label: "朝阳区\n高峰区" },
                    { x: "38%", y: "48%", size: 70, color: "rgba(245,158,11,0.5)", label: "海淀区\n中等区" },
                    { x: "68%", y: "28%", size: 60, color: "rgba(239,68,68,0.45)", label: "东城区\n高峰区" },
                    { x: "55%", y: "68%", size: 65, color: "rgba(34,197,94,0.4)", label: "丰台区\n低峰区" },
                    { x: "85%", y: "38%", size: 50, color: "rgba(245,158,11,0.45)", label: "石景山\n中等区" },
                    { x: "28%", y: "72%", size: 55, color: "rgba(34,197,94,0.35)", label: "西城区\n低峰区" },
                  ].map((blob, i) => (
                    <div key={i} className="absolute flex flex-col items-center" style={{ left: blob.x, top: blob.y, transform: "translate(-50%,-50%)" }}>
                      <div className="rounded-full" style={{ width: blob.size, height: blob.size, background: `radial-gradient(circle, ${blob.color} 0%, transparent 70%)`, filter: "blur(8px)" }} />
                      <div className="absolute text-center" style={{ top: "50%", transform: "translateY(-50%)" }}>
                        <div className="text-white text-[8px] font-bold leading-tight whitespace-pre-line drop-shadow">{blob.label}</div>
                      </div>
                    </div>
                  ))}
                  {/* Legend */}
                  <div className="absolute bottom-3 left-3 bg-black/60 rounded-lg px-3 py-2">
                    <div className="text-white/60 text-[9px] mb-1.5">区域用气热力图</div>
                    <div className="flex items-center gap-1">
                      <div className="w-16 h-2 rounded-full" style={{ background: "linear-gradient(to right, rgba(34,197,94,0.8), rgba(245,158,11,0.8), rgba(239,68,68,0.8))" }} />
                    </div>
                    <div className="flex justify-between text-[8px] text-white/40 mt-0.5">
                      <span>低</span><span>中</span><span>高</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Pressure overlay */}
            {gisLayer === "pressure" && (
              <div className="absolute inset-0">
                {[
                  { x: "15%", y: "20%", val: "0.35", status: "正常", color: "#22c55e" },
                  { x: "40%", y: "45%", val: "0.28", status: "正常", color: "#22c55e" },
                  { x: "65%", y: "30%", val: "0.41", status: "超压", color: "#ef4444" },
                  { x: "55%", y: "65%", val: "0.22", status: "偏低", color: "#f59e0b" },
                  { x: "82%", y: "35%", val: "0.33", status: "正常", color: "#22c55e" },
                ].map((p, i) => (
                  <div key={i} className="absolute flex flex-col items-center" style={{ left: p.x, top: p.y, transform: "translate(-50%,-50%)" }}>
                    <div className="rounded-full border-2 flex items-center justify-center" style={{ width: 44, height: 44, borderColor: p.color, background: "rgba(0,0,0,0.7)" }}>
                      <div className="text-center">
                        <div className="font-bold text-[10px]" style={{ color: p.color }}>{p.val}</div>
                        <div className="text-white/40 text-[7px]">MPa</div>
                      </div>
                    </div>
                    <div className="text-[9px] mt-0.5 font-medium" style={{ color: p.color }}>{p.status}</div>
                  </div>
                ))}
                <div className="absolute bottom-3 left-3 bg-black/60 rounded-lg px-3 py-2 text-[9px] text-white/60">
                  管网压力实时叠加层 · 单位 MPa
                </div>
              </div>
            )}
            <div className="absolute bottom-3 left-3 right-3 flex gap-2">
              {[
                { label: "管网里程", value: "2,856 km", icon: "ri-route-line" },
                { label: "调压站", value: "3 座", icon: "ri-building-line" },
                { label: "压力监测点", value: "4 个", icon: "ri-speed-line" },
                { label: "阀门", value: "12 个", icon: "ri-settings-line" },
              ].map((s, i) => (
                <div key={i} className="flex-1 bg-black/60 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
                  <i className={`${s.icon} text-[#E8531A] text-xs`}></i>
                  <div>
                    <div className="text-white text-xs font-bold">{s.value}</div>
                    <div className="text-white/40 text-[9px]">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom two charts side by side */}
          <div className="grid grid-cols-2 gap-3">
            {/* Real-time flow line chart */}
            <div className="rounded-xl border border-white/10 p-3" style={{ background: "rgba(255,255,255,0.04)", height: 130 }}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs text-white/60 flex items-center gap-2">
                  <i className="ri-pulse-line text-cyan-400"></i>
                  实时流量动态曲线
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse"></div>
                  <span className="text-[10px] text-cyan-400">LIVE</span>
                </div>
              </div>
              <canvas ref={flowCanvasRef} width={240} height={88} className="w-full" />
            </div>

            {/* Gas usage AI prediction comparison */}
            <div className="rounded-xl border border-white/10 p-3" style={{ background: "rgba(255,255,255,0.04)", height: 130 }}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs text-white/60 flex items-center gap-2">
                  <i className="ri-robot-2-line text-[#E8531A]"></i>
                  用气量AI预测对比（万m³）
                </div>
                <span className="text-[10px] text-[#E8531A] bg-[#E8531A]/10 px-2 py-0.5 rounded-full">误差 &lt;2.1%</span>
              </div>
              <canvas ref={gasCanvasRef} width={240} height={88} className="w-full" />
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="col-span-3 flex flex-col gap-3">
          {/* Region gas usage live ranking */}
          <div className="rounded-xl border border-white/10 p-3 flex-1" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="text-xs text-white/60 mb-2 flex items-center gap-2">
              <i className="ri-bar-chart-grouped-line text-[#E8531A]"></i>
              区域用气量实时排行
              <div className="ml-auto flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
                <span className="text-[10px] text-green-400">实时</span>
              </div>
            </div>
            <div className="space-y-1.5">
              {liveRegionData.map((r, i) => (
                <div key={r.name} className="transition-all duration-700">
                  <div className="flex items-center justify-between text-xs mb-0.5">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-4 h-4 rounded flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${i < 3 ? "bg-[#E8531A] text-white" : "bg-white/10 text-white/50"}`}>{r.rank}</span>
                      <span className="text-white/80">{r.name}</span>
                      <span className={`text-[9px] flex items-center gap-0.5 ${r.trend === "up" ? "text-green-400" : "text-red-400"}`}>
                        <i className={r.trend === "up" ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                        {r.delta}
                      </span>
                    </div>
                    <span className="text-[#E8531A] font-bold text-[11px]">{r.usage.toFixed(1)}<span className="text-white/30 text-[9px] ml-0.5">万m³</span></span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-1">
                    <div className="h-1 rounded-full transition-all duration-700"
                      style={{ width: `${(r.usage / liveRegionData[0].usage) * 100}%`, background: i < 3 ? "#E8531A" : "rgba(232,83,26,0.4)" }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Confidence Trend */}
          <div className="rounded-xl border border-[#E8531A]/20 p-3" style={{ background: "rgba(232,83,26,0.04)" }}>
            <div className="text-xs text-white/60 mb-1.5 flex items-center gap-2">
              <i className="ri-robot-2-line text-[#E8531A]"></i>
              AI预测置信度趋势
              <span className="ml-auto text-[10px] text-[#E8531A] font-bold">{aiConfidenceHistory[aiConfidenceHistory.length - 1]}%</span>
            </div>
            <div className="flex items-end gap-0.5 h-10">
              {aiConfidenceHistory.map((v, i) => {
                const isLast = i === aiConfidenceHistory.length - 1;
                const h = Math.max(4, ((v - 55) / 45) * 100);
                return (
                  <div key={i} className="flex-1 rounded-sm transition-all duration-500"
                    style={{ height: `${h}%`, background: isLast ? "#E8531A" : `rgba(232,83,26,${0.3 + (i / aiConfidenceHistory.length) * 0.5})` }} />
                );
              })}
            </div>
            <div className="flex justify-between text-[9px] text-white/30 mt-1">
              <span>20步前</span><span>当前</span>
            </div>
          </div>

          {/* Gas type distribution */}
          <div className="rounded-xl border border-white/10 p-3" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="text-xs text-white/60 mb-2 flex items-center gap-2">
              <i className="ri-pie-chart-line text-[#E8531A]"></i>
              用户类型分布
            </div>
            <div className="flex items-center gap-4">
              <div className="relative w-16 h-16 flex-shrink-0">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E8531A" strokeWidth="3"
                    strokeDasharray="89.7 10.3" strokeLinecap="round" />
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f59e0b" strokeWidth="3"
                    strokeDasharray="10.3 89.7" strokeDashoffset="-89.7" strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">128K</span>
                </div>
              </div>
              <div className="space-y-1.5 flex-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-[#E8531A]"></div>
                    <span className="text-white/60 text-xs">居民用户</span>
                  </div>
                  <span className="text-white text-xs font-bold">89.7%</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                    <span className="text-white/60 text-xs">工商业</span>
                  </div>
                  <span className="text-white text-xs font-bold">10.3%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly target */}
          <div className="rounded-xl border border-white/10 p-3" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="text-xs text-white/60 mb-2 flex items-center gap-2">
              <i className="ri-focus-3-line text-[#E8531A]"></i>
              本月目标完成率
            </div>
            <div className="space-y-2">
              {[
                { label: "营收目标", current: 856.2, target: 900, unit: "万" },
                { label: "用气量", current: 285.6, target: 300, unit: "万m³" },
                { label: "新增用户", current: 1256, target: 1500, unit: "户" },
              ].map((item, i) => {
                const pct = Math.min((item.current / item.target) * 100, 100);
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-white/60">{item.label}</span>
                      <span className="text-white/80">{pct.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full transition-all"
                        style={{ width: `${pct}%`, background: pct >= 90 ? "#22c55e" : pct >= 70 ? "#E8531A" : "#ef4444" }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* AI Prediction */}
          <div className="rounded-xl border border-[#E8531A]/30 p-3" style={{ background: "rgba(232,83,26,0.05)" }}>
            <div className="text-xs text-white/60 mb-2 flex items-center gap-2">
              <i className="ri-robot-2-line text-[#E8531A]"></i>
              AI智能预测
            </div>
            <div className="space-y-2">
              {[
                { label: "下月营收预测", value: "¥910万", trend: "+6.3%", up: true },
                { label: "欠费风险用户", value: "28户", trend: "-5户", up: false },
                { label: "设备故障预警", value: "3台", trend: "需关注", up: false },
                { label: "用气量预测", value: "295万m³", trend: "+3.3%", up: true },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-white/50">{item.label}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-white font-bold">{item.value}</span>
                    <span className={`text-[10px] ${item.up ? "text-green-400" : "text-amber-400"}`}>{item.trend}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Real-time Alarm Popups - bottom right stack */}
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none" style={{ maxWidth: 340 }}>
      {alarmPopups.filter(a => a.visible).map(alarm => (
        <div
          key={alarm.id}
          className="pointer-events-auto rounded-xl border overflow-hidden transition-all duration-500"
          style={{
            background: alarm.level === "danger" ? "rgba(15,10,30,0.97)" : alarm.level === "warning" ? "rgba(15,10,30,0.97)" : "rgba(15,10,30,0.97)",
            borderColor: alarm.level === "danger" ? "rgba(239,68,68,0.6)" : alarm.level === "warning" ? "rgba(245,158,11,0.6)" : "rgba(6,182,212,0.6)",
          }}
        >
          {/* Top bar */}
          <div
            className="flex items-center justify-between px-3 py-2"
            style={{ background: alarm.level === "danger" ? "rgba(239,68,68,0.15)" : alarm.level === "warning" ? "rgba(245,158,11,0.12)" : "rgba(6,182,212,0.12)" }}
          >
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full animate-pulse ${alarm.level === "danger" ? "bg-red-500" : alarm.level === "warning" ? "bg-amber-500" : "bg-cyan-400"}`}></div>
              <span className={`text-xs font-bold ${alarm.level === "danger" ? "text-red-400" : alarm.level === "warning" ? "text-amber-400" : "text-cyan-400"}`}>
                {alarm.level === "danger" ? "紧急告警" : alarm.level === "warning" ? "预警通知" : "系统通知"}
              </span>
              <span className="text-white/30 text-[10px]">{alarm.time}</span>
            </div>
            <button
              onClick={() => setAlarmPopups(current => current.map(a => a.id === alarm.id ? { ...a, visible: false } : a))}
              className="w-5 h-5 flex items-center justify-center rounded cursor-pointer hover:bg-white/10"
            >
              <i className="ri-close-line text-white/50 text-xs"></i>
            </button>
          </div>
          {/* Content */}
          <div className="px-3 py-2.5">
            <div className="text-white text-sm font-bold mb-0.5">{alarm.title}</div>
            <div className="flex items-center gap-1 mb-1.5">
              <i className="ri-map-pin-line text-white/40 text-xs"></i>
              <span className="text-white/50 text-[11px]">{alarm.location}</span>
            </div>
            <div className="text-white/60 text-[11px] leading-relaxed mb-2">{alarm.detail}</div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowAlarmDetail(alarm)}
                className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium cursor-pointer whitespace-nowrap ${alarm.level === "danger" ? "bg-red-500/20 text-red-400 hover:bg-red-500/30" : "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"}`}
              >
                查看详情
              </button>
              <button
                onClick={() => setAlarmPopups(current => current.map(a => a.id === alarm.id ? { ...a, visible: false } : a))}
                className="flex-1 py-1.5 rounded-lg text-[11px] text-white/40 hover:text-white/60 cursor-pointer whitespace-nowrap border border-white/10"
              >
                忽略
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* AI Prediction Popup - top center */}
    {aiPrediction?.visible && (
      <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 w-[480px] rounded-xl border border-[#E8531A]/40 overflow-hidden"
        style={{ background: "rgba(10,8,25,0.97)" }}>
        <div className="flex items-center justify-between px-4 py-2.5" style={{ background: "rgba(232,83,26,0.12)" }}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#E8531A]/20">
              <i className="ri-robot-2-line text-[#E8531A] text-sm"></i>
            </div>
            <span className="text-[#E8531A] text-xs font-bold">AI异常预测预警</span>
            <span className="text-white/30 text-[10px]">置信度 {aiPrediction.confidence}%</span>
          </div>
          <button onClick={() => setAIPrediction(null)} className="w-5 h-5 flex items-center justify-center rounded cursor-pointer hover:bg-white/10">
            <i className="ri-close-line text-white/50 text-xs"></i>
          </button>
        </div>
        <div className="px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-white font-bold text-sm">{aiPrediction.type}</span>
                <div className="flex items-center gap-1 bg-[#E8531A]/15 px-2 py-0.5 rounded-full">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#E8531A] animate-pulse"></div>
                  <span className="text-[#E8531A] text-[10px] font-medium">高风险</span>
                </div>
              </div>
              <div className="flex items-center gap-1 mb-2">
                <i className="ri-map-pin-line text-white/40 text-xs"></i>
                <span className="text-white/50 text-xs">{aiPrediction.location}</span>
              </div>
              <p className="text-white/60 text-xs leading-relaxed mb-2">{aiPrediction.description}</p>
              <div className="bg-[#E8531A]/10 rounded-lg px-3 py-2 flex items-start gap-2">
                <i className="ri-lightbulb-line text-[#E8531A] text-sm flex-shrink-0 mt-0.5"></i>
                <span className="text-white/70 text-xs leading-relaxed">{aiPrediction.suggestion}</span>
              </div>
            </div>
            {/* Confidence ring */}
            <div className="flex-shrink-0 flex flex-col items-center gap-1">
              <div className="relative w-14 h-14">
                <svg viewBox="0 0 56 56" className="w-full h-full -rotate-90">
                  <circle cx="28" cy="28" r="22" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
                  <circle cx="28" cy="28" r="22" fill="none" stroke="#E8531A" strokeWidth="4"
                    strokeDasharray={`${(aiPrediction.confidence / 100) * 138.2} 138.2`}
                    strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{aiPrediction.confidence}%</span>
                </div>
              </div>
              <span className="text-white/40 text-[9px]">置信度</span>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button className="flex-1 bg-[#E8531A] text-white py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-[#d44a15]">
              <i className="ri-send-plane-line mr-1"></i>立即处置
            </button>
            <button className="flex-1 border border-white/15 text-white/60 py-2 rounded-lg text-xs cursor-pointer whitespace-nowrap hover:border-white/30">
              <i className="ri-file-list-3-line mr-1"></i>生成报告
            </button>
            <button onClick={() => setAIPrediction(null)} className="flex-1 border border-white/10 text-white/40 py-2 rounded-lg text-xs cursor-pointer whitespace-nowrap hover:border-white/20">
              稍后处理
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Alarm Detail Modal */}
    {showAlarmDetail && (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowAlarmDetail(null)}>
        <div className="w-[460px] rounded-2xl border overflow-hidden" style={{ background: "rgba(10,8,25,0.99)", borderColor: showAlarmDetail.level === "danger" ? "rgba(239,68,68,0.5)" : "rgba(245,158,11,0.5)" }} onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-5 py-3.5" style={{ background: showAlarmDetail.level === "danger" ? "rgba(239,68,68,0.12)" : "rgba(245,158,11,0.1)" }}>
            <div className="flex items-center gap-2">
              <i className={`ri-alarm-warning-line text-lg ${showAlarmDetail.level === "danger" ? "text-red-400" : "text-amber-400"}`}></i>
              <span className="text-white font-bold text-sm">{showAlarmDetail.title}</span>
            </div>
            <button onClick={() => setShowAlarmDetail(null)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 cursor-pointer">
              <i className="ri-close-line text-white/60"></i>
            </button>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "告警位置", value: showAlarmDetail.location, icon: "ri-map-pin-line" },
                { label: "告警时间", value: showAlarmDetail.time, icon: "ri-time-line" },
                { label: "告警级别", value: showAlarmDetail.level === "danger" ? "紧急" : showAlarmDetail.level === "warning" ? "预警" : "通知", icon: "ri-error-warning-line" },
                { label: "处理状态", value: "待处理", icon: "ri-loader-4-line" },
              ].map((item, i) => (
                <div key={i} className="bg-white/5 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <i className={`${item.icon} text-white/40 text-xs`}></i>
                    <span className="text-white/40 text-[10px]">{item.label}</span>
                  </div>
                  <div className="text-white text-xs font-medium">{item.value}</div>
                </div>
              ))}
            </div>
            <div className="bg-white/5 rounded-xl p-3">
              <div className="text-white/40 text-[10px] mb-1.5">告警详情</div>
              <div className="text-white/80 text-xs leading-relaxed">{showAlarmDetail.detail}</div>
            </div>
            <div className="bg-[#E8531A]/10 rounded-xl p-3 flex items-start gap-2">
              <i className="ri-lightbulb-line text-[#E8531A] text-sm flex-shrink-0 mt-0.5"></i>
              <div>
                <div className="text-[#E8531A] text-[10px] mb-0.5">AI处置建议</div>
                <div className="text-white/70 text-xs leading-relaxed">{showAlarmDetail.detail}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button className={`flex-1 py-2.5 rounded-xl text-xs font-medium cursor-pointer whitespace-nowrap ${showAlarmDetail.level === "danger" ? "bg-red-500 text-white hover:bg-red-600" : "bg-amber-500 text-white hover:bg-amber-600"}`}>
                <i className="ri-send-plane-line mr-1"></i>派单处理
              </button>
              <button className="flex-1 border border-white/15 text-white/60 py-2.5 rounded-xl text-xs cursor-pointer whitespace-nowrap hover:border-white/30">
                <i className="ri-phone-line mr-1"></i>联系负责人
              </button>
              <button onClick={() => setShowAlarmDetail(null)} className="flex-1 border border-white/10 text-white/40 py-2.5 rounded-xl text-xs cursor-pointer whitespace-nowrap">
                关闭
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}
